import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Syringe,
  CheckCircle2,
  AlertTriangle,
  Stethoscope,
  User,
  Users,
  Shield,
  Zap,
  Wind,
  AlertOctagon,
  Download,
  RotateCcw,
  Clock
} from 'lucide-react';

export default function IntubationPage() {
  const printRef = useRef(null);
  
  // Load checked items from localStorage
  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem('intubation-checklist');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  
  // Save to localStorage whenever checkedItems changes
  useEffect(() => {
    localStorage.setItem('intubation-checklist', JSON.stringify(checkedItems));
  }, [checkedItems]);

  const handleCheck = (section, item) => {
    setCheckedItems(prev => ({
      ...prev,
      [`${section}-${item}`]: !prev[`${section}-${item}`]
    }));
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser toute la checklist ?')) {
      setCheckedItems({});
      localStorage.removeItem('intubation-checklist');
      toast.success('Checklist réinitialisée');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF en cours...');
      
      const element = printRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Checklist-Intubation-ISR-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success('PDF exporté avec succès');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const checklistData = {
    patient: {
      title: "PATIENT",
      icon: User,
      items: [
        { id: "march", label: "Examen MARCH ou ABCDE", detail: "Évaluation initiale du patient" },
        { id: "collier", label: "Collier cervical, maintien du rachis", detail: "Protection rachidienne" },
        { id: "fracas", label: "Fracas dentaire/Aspiration", detail: "Vérification dentaire et aspiration disponible" },
        { id: "installation", label: "Installation/Ergonomie", detail: "Hauteur brancard, Position tête, BURP si besoin" },
        { id: "scope", label: "Scope visible 4 brins, SpO₂, EtCO₂, PA/2 min", detail: "Monitorage complet" },
        { id: "hemodynamique", label: "Optimisation hémodynamique", detail: "VVP bon calibre, Remplissage, Éphédrine, NAD" }
      ]
    },
    preoxygenation: {
      title: "PRÉ-OXYGÉNATION",
      objectif: "≥ 5 minutes, objectif SpO₂ > 95%",
      options: [
        { id: "vni", label: "VNI", detail: "Ventilation Non Invasive" },
        { id: "bavu", label: "BAVU", detail: "Ballon Autoremplisseur" },
        { id: "lunettes", label: "Lunettes O₂", detail: "Oxygène nasal" },
        { id: "mhc", label: "MHC", detail: "Masque Haute Concentration" },
        { id: "optiflow", label: "Optiflow", detail: "Haut débit nasal" }
      ]
    },
    materiel: {
      title: "MATÉRIEL BASIC",
      items: [
        "BAVU, EtCO₂, Masque, Filtres",
        "Aspiration-CH24",
        "Sonde mandrinée, Seringue 10cc, Laryngo, Lame",
        "Respirateur + O₂ + Paramètres préréglés"
      ]
    },
    medicaments: {
      title: "MÉDICAMENTS",
      categories: [
        {
          nom: "Induction",
          drugs: [
            { name: "KÉTAMINE", dose: "2-3 mg/kg", color: "text-cyan-300" },
            { name: "HYPNOMIDATE (Étomidate)", dose: "0,25-0,4 mg/kg", color: "text-blue-300" }
          ]
        },
        {
          nom: "Curare",
          drugs: [
            { name: "CÉLOCURINE", dose: "1 mg/kg", color: "text-purple-300" },
            { name: "ROCURONIUM", dose: "1 mg/kg", color: "text-purple-300" }
          ]
        },
        {
          nom: "Entretien",
          drugs: [
            { name: "HYPNOVEL", dose: "0,05-0,1 mg/kg/h", color: "text-green-300" },
            { name: "SUFENTANIL", dose: "0,5-1 µg/kg/h", color: "text-green-300" }
          ]
        }
      ],
      complications: [
        { label: "Atropine prête", detail: "En cas de bradycardie" },
        { label: "Plans B et C", detail: "Bougie > VL > LTSD > Cricothyroïdotomie" }
      ]
    },
    induction: {
      title: "INDUCTION-IOT",
      planifier: [
        "Rôle-position Plans B et C",
        "Communication BOUCLE FERMÉE"
      ],
      conducteur: {
        titre: "CONDUCTEUR prêt pour :",
        items: [
          "Maintien rachis",
          "BURP",
          "Passer matériel",
          "Brancher respirateur"
        ]
      },
      equipier: {
        titre: "ÉQUIPIER prêt pour :",
        items: [
          "Réoxygéner si SpO₂ < 80%",
          "Aspiration si vomissements"
        ]
      },
      medecin: {
        titre: "MÉDECIN",
        action: "Annonce doses KÉTAMINE + CÉLOCURINE"
      },
      ide: {
        titre: "IDE",
        action: "Répète, administre, confirme les doses"
      },
      attente: {
        titre: "Attente fasciculations ou 1 minute",
        checks: [
          "Exposition",
          "Cordes vocales visibles",
          "Intubation jusqu'au repère noir",
          "⚠️ 2 tentatives = stratégie alternative + renfort"
        ]
      }
    },
    postiot: {
      title: "POST IOT",
      parametres: [
        { label: "Chiffre arcade dentaire", unit: "cm" },
        { label: "Pression du ballonnet", unit: "mmHg" }
      ],
      mesures: [
        { label: "Saturation", unit: "%" },
        { label: "ETCO₂", unit: "mmHg" },
        { label: "PAS", unit: "mmHg" },
        { label: "PAD", unit: "mmHg" }
      ],
      controles: [
        { id: "etco2", label: "ETCO₂ courbe OK", critical: true },
        { id: "auscultation", label: "Auscultation 2 champs → si sélectif : retirer 2cm", critical: true },
        { id: "fixation", label: "Fixation sonde", critical: false },
        { id: "connexion", label: "Connexion respirateur", critical: false },
        { id: "sonde", label: "Sonde gastrique", critical: false }
      ],
      complications: {
        titre: "Problème patient/respirateur",
        liste: [
          { nom: "Débranchement", icon: AlertTriangle },
          { nom: "Obstruction", icon: AlertOctagon },
          { nom: "Pneumothorax", icon: AlertTriangle },
          { nom: "Sonde plicaturée", icon: AlertOctagon }
        ],
        traitement: "Si nécessaire : Rocuronium 0,15 mg/kg"
      },
      gonflage: {
        titre: "Gonfler le ballonnet",
        items: [
          "Première insufflation au BAVU",
          "Auscultation épigastrique",
          "Auscultation 2 champs pulmonaires",
          "EtCO₂ courbe stable sur 5 cycles"
        ]
      }
    },
    difficulte: {
      title: "SUSPICION IOT DIFFICILE",
      score: "Score MACOCHA",
      criteres: [
        "Mallampatti",
        "SAS",
        "Limitation mobilité rachis",
        "Ouverture buccale <3cm",
        "Coma",
        "Sat<80%"
      ],
      facteurs: [
        "Obésité",
        "Cou court",
        "Barbe",
        "Brûlure",
        "Trauma facial",
        "Hémoptysie",
        "Tumeur ORL"
      ],
      action: "⚠️ Appel à l'aide / renfort technique"
    }
  };

  // Calculate completion
  const totalItems = 
    checklistData.patient.items.length + 
    checklistData.preoxygenation.options.length +
    checklistData.induction.conducteur.items.length +
    checklistData.induction.equipier.items.length +
    checklistData.postiot.controles.length;
  
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const completionPercentage = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 max-w-5xl mx-auto" data-testid="intubation-page">
      {/* Header with Actions */}
      <div className="space-y-3 print:hidden">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-red-400/20 to-orange-500/20 flex items-center justify-center border border-red-400/30 flex-shrink-0">
              <Syringe className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-[hsl(var(--text))] break-words">
                Intubation Séquence Rapide
              </h1>
              <p className="text-xs md:text-sm text-[hsl(var(--text-dim))]">
                Aide cognitive ISR
              </p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleReset}
              variant="outline"
              size="sm"
              className="text-xs md:text-sm text-amber-300 border-amber-400/30 hover:bg-amber-400/10"
              data-testid="reset-checklist-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="ml-1">Reset</span>
            </Button>
            <Button
              onClick={handleExportPDF}
              variant="default"
              size="sm"
              className="text-xs md:text-sm bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/30"
              data-testid="export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="ml-1">PDF</span>
            </Button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="text-[hsl(var(--text-dim))]">Progression</span>
            <span className="font-mono font-bold text-cyan-300">{completionPercentage}% ({checkedCount}/{totalItems})</span>
          </div>
          <div className="h-2 bg-[hsl(var(--bg-elev))] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-green-400 transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
        
        <Alert className="bg-red-400/10 border-red-400/30">
          <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-400" />
          <AlertDescription className="text-xs md:text-sm text-[hsl(var(--text))]">
            <strong>Procédure à haut risque :</strong> Vérifier chaque étape
          </AlertDescription>
        </Alert>
      </div>

      {/* Print Header (only visible in PDF) */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-center mb-2">Check-list Intubation Séquence Rapide (ISR)</h1>
        <p className="text-center text-sm text-gray-600">Date: {new Date().toLocaleDateString('fr-FR')}</p>
        <p className="text-center text-sm text-gray-600">Progression: {completionPercentage}% ({checkedCount}/{totalItems} items cochés)</p>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="space-y-3 md:space-y-4">
        {/* PATIENT */}
        <Card className="border-[hsl(var(--border))] print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-gray-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-cyan-300 print:text-gray-800 flex items-center gap-2">
              <User className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.patient.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4">
            <div className="space-y-1.5 md:space-y-2">
              {checklistData.patient.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-2 p-2 md:p-2.5 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white hover:bg-[hsl(var(--bg-elev))]/80 transition-colors cursor-pointer active:scale-[0.98] print:cursor-default print:active:scale-100 print:border print:border-gray-200"
                  onClick={() => handleCheck('patient', item.id)}
                  data-testid={`check-patient-${item.id}`}
                >
                  <Checkbox
                    checked={checkedItems[`patient-${item.id}`] || false}
                    className="mt-0.5 flex-shrink-0 print:border-gray-400"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words leading-tight">{item.label}</div>
                    <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600 break-words mt-0.5">{item.detail}</div>
                  </div>
                  {checkedItems[`patient-${item.id}`] && (
                    <CheckCircle2 className="w-4 h-4 md:w-4.5 md:h-4.5 text-green-400 print:text-green-600 flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* PRÉ-OXYGÉNATION */}
        <Card className="border-cyan-400/30 bg-cyan-400/5 print:bg-blue-50 print:border-blue-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-blue-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-cyan-300 print:text-blue-800 flex items-center gap-2">
              <Wind className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.preoxygenation.title}
            </CardTitle>
            <div className="text-xs md:text-sm text-amber-300 print:text-amber-700 mt-1 flex items-center gap-1 font-medium">
              <Clock className="w-3 h-3 md:w-3.5 md:h-3.5" />
              {checklistData.preoxygenation.objectif}
            </div>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 md:gap-2">
              {checklistData.preoxygenation.options.map((option) => (
                <div
                  key={option.id}
                  className="flex items-start gap-1.5 md:gap-2 p-1.5 md:p-2 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white hover:bg-[hsl(var(--bg-elev))]/80 transition-colors cursor-pointer border border-[hsl(var(--border))] print:border-gray-200 active:scale-[0.98] print:active:scale-100"
                  onClick={() => handleCheck('preox', option.id)}
                  data-testid={`check-preox-${option.id}`}
                >
                  <Checkbox checked={checkedItems[`preox-${option.id}`] || false} className="flex-shrink-0 mt-0.5 print:border-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">{option.label}</div>
                    <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600 truncate">{option.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* MATÉRIEL */}
        <Card className="border-[hsl(var(--border))] print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-gray-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-cyan-300 print:text-gray-800 flex items-center gap-2">
              <Shield className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.materiel.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4">
            <div className="space-y-1.5">
              {checklistData.materiel.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 p-2 rounded bg-[hsl(var(--bg-elev))] print:bg-white print:border print:border-gray-200 cursor-pointer active:scale-[0.98] print:active:scale-100"
                  onClick={() => handleCheck('materiel', i)}
                >
                  <Checkbox checked={checkedItems[`materiel-${i}`] || false} className="flex-shrink-0 mt-0.5 print:border-gray-400" />
                  <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words leading-tight">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* MÉDICAMENTS - Accordion on mobile */}
        <Card className="border-purple-400/30 print:border-purple-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-purple-50 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-purple-300 print:text-purple-800 flex items-center gap-2">
              <Syringe className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.medicaments.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4 space-y-2 md:space-y-3">
            <Accordion type="multiple" defaultValue={["med-0", "med-1", "med-2"]} className="w-full space-y-2 print:space-y-3">
              {checklistData.medicaments.categories.map((category, idx) => (
                <AccordionItem key={idx} value={`med-${idx}`} className="border border-[hsl(var(--border))] print:border-gray-300 rounded-lg overflow-hidden print:border-none">
                  <AccordionTrigger className="px-2.5 md:px-3 py-2 text-xs md:text-sm hover:bg-[hsl(var(--bg-elev))] print:hover:bg-transparent print:pb-2">
                    <span className="font-semibold text-[hsl(var(--text))] print:text-gray-900">{category.nom}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-2.5 md:px-3 pb-2 md:pb-3">
                    <div className="space-y-1.5">
                      {category.drugs.map((drug, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 p-1.5 md:p-2 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200">
                          <span className="font-medium text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words">{drug.name}</span>
                          <Badge className={`${drug.color} print:bg-gray-200 print:text-gray-900 bg-opacity-20 font-mono text-[10px] md:text-xs w-fit`}>
                            {drug.dose}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="pt-2 md:pt-3 border-t border-[hsl(var(--border))] print:border-gray-300 space-y-1.5">
              <h3 className="font-semibold text-xs md:text-sm text-amber-300 print:text-amber-700">Complications</h3>
              {checklistData.medicaments.complications.map((comp, i) => (
                <div key={i} className="flex items-start gap-1.5 md:gap-2 p-1.5 md:p-2 rounded bg-amber-500/10 print:bg-amber-50 border border-amber-500/20 print:border-amber-300">
                  <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-400 print:text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 break-words">{comp.label}</div>
                    <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600 break-words">{comp.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* PROCÉDURE INDUCTION */}
        <Card className="border-[hsl(var(--border))] print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-gray-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-orange-300 print:text-gray-800 flex items-center gap-2">
              <Zap className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.induction.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4 space-y-2 md:space-y-3">
            {/* Planifier */}
            <div className="p-2 md:p-2.5 rounded-lg bg-blue-500/10 print:bg-blue-50 border border-blue-400/30 print:border-blue-300">
              <h3 className="font-semibold text-xs md:text-sm text-blue-300 print:text-blue-800 mb-1.5">PLANIFIER :</h3>
              <div className="space-y-0.5 md:space-y-1">
                {checklistData.induction.planifier.map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-blue-400 print:text-blue-600 flex-shrink-0" />
                    <span className="break-words">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Conducteur et Équipier */}
            <div className="space-y-2 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
              <div className="p-2 md:p-2.5 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-300">
                <h3 className="font-semibold text-xs md:text-sm text-cyan-300 print:text-gray-800 mb-1.5 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 md:w-4 md:h-4 print:hidden" />
                  {checklistData.induction.conducteur.titre}
                </h3>
                <div className="space-y-1">
                  {checklistData.induction.conducteur.items.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm cursor-pointer active:scale-[0.98] print:active:scale-100"
                      onClick={() => handleCheck('conducteur', i)}
                    >
                      <Checkbox checked={checkedItems[`conducteur-${i}`] || false} className="flex-shrink-0 print:border-gray-400" />
                      <span className="text-[hsl(var(--text))] print:text-gray-900 break-words">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-2 md:p-2.5 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-300">
                <h3 className="font-semibold text-xs md:text-sm text-cyan-300 print:text-gray-800 mb-1.5 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 md:w-4 md:h-4 print:hidden" />
                  {checklistData.induction.equipier.titre}
                </h3>
                <div className="space-y-1">
                  {checklistData.induction.equipier.items.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm cursor-pointer active:scale-[0.98] print:active:scale-100"
                      onClick={() => handleCheck('equipier', i)}
                    >
                      <Checkbox checked={checkedItems[`equipier-${i}`] || false} className="flex-shrink-0 print:border-gray-400" />
                      <span className="text-[hsl(var(--text))] print:text-gray-900 break-words">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Médecin et IDE */}
            <div className="space-y-2">
              <div className="p-2 md:p-2.5 rounded-lg bg-purple-500/10 print:bg-purple-50 border border-purple-400/30 print:border-purple-300">
                <h3 className="font-semibold text-xs md:text-sm text-purple-300 print:text-purple-800 mb-1">{checklistData.induction.medecin.titre}</h3>
                <p className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words">{checklistData.induction.medecin.action}</p>
              </div>
              <div className="p-2 md:p-2.5 rounded-lg bg-green-500/10 print:bg-green-50 border border-green-400/30 print:border-green-300">
                <h3 className="font-semibold text-xs md:text-sm text-green-300 print:text-green-800 mb-1">{checklistData.induction.ide.titre}</h3>
                <p className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words">{checklistData.induction.ide.action}</p>
              </div>
            </div>

            {/* Attente */}
            <div className="p-2.5 md:p-3 rounded-lg bg-orange-500/10 print:bg-orange-50 border border-orange-400/30 print:border-orange-300">
              <h3 className="font-semibold text-xs md:text-sm text-orange-300 print:text-orange-800 mb-2">{checklistData.induction.attente.titre}</h3>
              <div className="space-y-1">
                {checklistData.induction.attente.checks.map((check, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-orange-400 print:text-orange-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words leading-tight">{check}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gonflage ballonnet */}
            <div className="p-2.5 md:p-3 rounded-lg bg-blue-500/10 print:bg-blue-50 border border-blue-400/30 print:border-blue-300">
              <h3 className="font-semibold text-xs md:text-sm text-blue-300 print:text-blue-800 mb-2">{checklistData.postiot.gonflage.titre}</h3>
              <div className="space-y-1">
                {checklistData.postiot.gonflage.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-blue-400 print:text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 break-words leading-tight">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* POST IOT */}
        <Card className="border-green-400/30 print:border-green-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-green-50 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-green-300 print:text-green-800 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.postiot.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4 space-y-2 md:space-y-3">
            {/* Paramètres */}
            <div className="grid grid-cols-2 gap-2">
              {checklistData.postiot.parametres.map((param, i) => (
                <div key={i} className="p-2 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200">
                  <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-0.5 truncate">{param.label}</div>
                  <div className="text-xs text-cyan-300 print:text-cyan-700 font-mono">{param.unit}</div>
                </div>
              ))}
            </div>

            {/* Mesures vitales */}
            <div>
              <h3 className="text-xs md:text-sm font-semibold text-[hsl(var(--text))] print:text-gray-900 mb-1.5">Paramètres vitaux</h3>
              <div className="grid grid-cols-2 gap-2">
                {checklistData.postiot.mesures.map((mesure, i) => (
                  <div key={i} className="p-2 rounded-lg bg-green-500/10 print:bg-green-50 border border-green-400/30 print:border-green-300">
                    <div className="text-[10px] md:text-xs text-[hsl(var(--text-dim))] print:text-gray-600 mb-0.5 truncate">{mesure.label}</div>
                    <div className="text-xs text-green-300 print:text-green-700 font-mono">{mesure.unit}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contrôles */}
            <div>
              <h3 className="text-xs md:text-sm font-semibold text-[hsl(var(--text))] print:text-gray-900 mb-2">CONTRÔLES</h3>
              <div className="space-y-1.5">
                {checklistData.postiot.controles.map((controle) => (
                  <div
                    key={controle.id}
                    className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors active:scale-[0.98] print:active:scale-100 ${
                      controle.critical 
                        ? 'bg-red-500/10 print:bg-red-50 border border-red-400/30 print:border-red-300 hover:bg-red-500/15' 
                        : 'bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-200 hover:bg-[hsl(var(--bg-elev))]/80'
                    }`}
                    onClick={() => handleCheck('postiot', controle.id)}
                    data-testid={`check-postiot-${controle.id}`}
                  >
                    <Checkbox checked={checkedItems[`postiot-${controle.id}`] || false} className="flex-shrink-0 mt-0.5 print:border-gray-400" />
                    <span className={`text-xs md:text-sm flex-1 break-words leading-tight ${controle.critical ? 'text-red-300 print:text-red-800 font-medium' : 'text-[hsl(var(--text))] print:text-gray-900'}`}>
                      {controle.label}
                    </span>
                    {controle.critical && <AlertTriangle className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-400 print:text-red-600 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Complications */}
            <div className="p-2.5 md:p-3 rounded-lg bg-red-500/10 print:bg-red-50 border border-red-400/30 print:border-red-300">
              <h3 className="font-semibold text-xs md:text-sm text-red-300 print:text-red-800 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 md:w-4.5 md:h-4.5" />
                {checklistData.postiot.complications.titre}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-2 md:mb-3">
                {checklistData.postiot.complications.liste.map((comp, i) => {
                  const IconComp = comp.icon;
                  return (
                    <div key={i} className="flex items-center gap-1.5 p-1.5 rounded bg-[hsl(var(--bg-elev))] print:bg-white print:border print:border-gray-200">
                      <IconComp className="w-3 h-3 md:w-3.5 md:h-3.5 text-red-400 print:text-red-600 flex-shrink-0" />
                      <span className="text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 truncate">{comp.nom}</span>
                    </div>
                  );
                })}
              </div>
              <div className="p-2 rounded bg-purple-500/20 print:bg-purple-100 border border-purple-400/30 print:border-purple-300">
                <div className="text-[10px] md:text-xs text-purple-300 print:text-purple-800 mb-0.5 font-semibold">TRAITEMENT</div>
                <div className="text-xs md:text-sm font-medium text-[hsl(var(--text))] print:text-gray-900 break-words">
                  {checklistData.postiot.complications.traitement}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SUSPICION IOT DIFFICILE */}
        <Card className="border-amber-400/30 bg-amber-400/5 print:bg-amber-50 print:border-amber-300 print:break-inside-avoid">
          <CardHeader className="bg-[hsl(var(--bg-elev))] print:bg-amber-100 p-2.5 md:p-4">
            <CardTitle className="text-sm md:text-base text-amber-300 print:text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 print:hidden" />
              {checklistData.difficulte.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 md:pt-3 p-2.5 md:p-4 space-y-2 md:space-y-3">
            {/* Score MACOCHA */}
            <div className="p-2 md:p-2.5 rounded-lg bg-[hsl(var(--bg-elev))] print:bg-white border border-[hsl(var(--border))] print:border-gray-300">
              <h3 className="font-semibold text-xs md:text-sm text-cyan-300 print:text-gray-800 mb-1.5">{checklistData.difficulte.score}</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {checklistData.difficulte.criteres.map((critere, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs md:text-sm">
                    <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-cyan-400 print:text-cyan-600 flex-shrink-0" />
                    <span className="text-[hsl(var(--text))] print:text-gray-900 truncate">{critere}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Facteurs */}
            <div>
              <h3 className="font-semibold text-xs md:text-sm text-[hsl(var(--text))] print:text-gray-900 mb-1.5">Autres facteurs</h3>
              <div className="flex flex-wrap gap-1.5">
                {checklistData.difficulte.facteurs.map((facteur, i) => (
                  <Badge key={i} variant="secondary" className="bg-amber-500/20 print:bg-amber-200 text-amber-300 print:text-amber-900 text-[10px] md:text-xs">
                    {facteur}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Action */}
            <Alert className="bg-red-400/10 print:bg-red-100 border-red-400/30 print:border-red-400 p-2.5 md:p-3">
              <AlertTriangle className="h-3.5 w-3.5 md:h-4 md:w-4 text-red-400 print:text-red-700" />
              <AlertDescription className="text-xs md:text-sm text-[hsl(var(--text))] print:text-red-900 font-medium">
                {checklistData.difficulte.action}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-xs text-gray-600">
        <p>Check-list Intubation Séquence Rapide - UrgenceMed</p>
        <p>Document généré le {new Date().toLocaleString('fr-FR')}</p>
      </div>
    </div>
  );
}
